import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  User,
  AlertCircle,
  Home,
  Key,
} from "lucide-react";
import apiClient from "../Api/apiConfig";
import "../styles/RegisterPage.css";

const generatePassword = () => {
  const length = 12;
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  return Array.from(
    { length },
    () => charset[Math.floor(Math.random() * charset.length)]
  ).join("");
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ⭐ Rôle sélectionné (locataire / propriétaire / user standard)
  const [role, setRole] = useState(location.state?.role || "tenant");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    tenant_number: "", // Optionnel
    landlord_id: "", // Optionnel
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [lords, setLords] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // pour le submit du formulaire
  const [isSavingUser, setIsSavingUser] = useState(false); // pour le "J’ai compris"
  const [isUserExists, setIsUserExists] = useState(false);
  const [showAdhesionModal, setShowAdhesionModal] = useState(false);
  const [showEmailConfirmationModal, setShowEmailConfirmationModal] =
    useState(false);

  // 🔁 Redirection différée après "J’ai compris"
  const [pendingRedirect, setPendingRedirect] = useState(null);
  // ex: { path: "/adhesion", withFormData: true } ou null

  // ✅ Toast géré DANS le composant
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Met à jour le rôle si on arrive avec un state différent
  useEffect(() => {
    if (location.state?.role && location.state.role !== role) {
      setRole(location.state.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.role]);

  // Charger les bailleurs seulement si role = tenant
  useEffect(() => {
    if (role === "tenant") {
      const fetchLandlords = async () => {
        try {
          const response = await apiClient.get("/landlords");
          setLords(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
          console.error(
            "❌ Erreur chargement bailleurs API:",
            err.response?.data || err.message
          );
          setLords([]);
        }
      };
      fetchLandlords();
    } else {
      setLords([]);
    }
  }, [role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData((prev) => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name) newErrors.first_name = "Requis";
    if (!formData.last_name) newErrors.last_name = "Requis";
    if (!formData.email) newErrors.email = "Requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email invalide";
    if (!formData.phone) newErrors.phone = "Requis";
    if (!formData.address) newErrors.address = "Requis";
    if (!formData.postal_code) newErrors.postal_code = "Requis";
    if (!formData.city) newErrors.city = "Requis";

    // tenant_number & landlord_id restent optionnels

    if (!formData.password) newErrors.password = "Requis";
    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "Ne correspond pas";
    return newErrors;
  };

  const checkUserExists = async (email) => {
    try {
      const response = await apiClient.get(`/users/check-email?email=${email}`);
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  // 🔁 Choix Adhésion : on NE redirige plus tout de suite
  const handleAdhesionChoice = (choice) => {
    if (choice === "yes") {
      // On prépare une redirection vers la page d'adhésion
      setPendingRedirect({
        path: "/adhesion",
        withFormData: true,
      });
    } else {
      // Pas d'adhésion maintenant → retour simple à l'accueil après "J’ai compris"
      setPendingRedirect(null);
    }

    // On ferme le modal d’adhésion
    setShowAdhesionModal(false);
    // Et on affiche le modal de confirmation d’email
    setShowEmailConfirmationModal(true);
  };

  // ✅ Clic sur "J’ai compris" (on crée l’utilisateur ICI + envoi email + redirection)
  const handleEmailConfirmationOk = async () => {
    // ⚠️ Empêche les clics multiples
    if (isSavingUser) return;

    setIsSavingUser(true);

    try {
      // Préparation des données avec le rôle
      let payload = {
        ...formData,
        role: role,
        tenant_number: null,
        landlord_id: null,
      };

      if (role === "tenant") {
        payload = {
          ...payload,
          tenant_number: formData.tenant_number || null,
          landlord_id: formData.landlord_id || null,
        };
      }

      // 👉 C'est ici seulement qu'on crée l'utilisateur + envoi email
      await apiClient.post("/users/register", payload);

      // On ferme le modal
      setShowEmailConfirmationModal(false);

      // Toast d’info après l’OK
      showToast(
        `Un email de confirmation a été envoyé à ${formData.email}.`,
        "success"
      );

      // Redirection après validation :
      // - si pendingRedirect existe (adhésion), on y va
      // - sinon, retour à la page d’accueil "/"
      if (pendingRedirect?.path) {
        if (pendingRedirect.withFormData) {
          navigate(pendingRedirect.path, { state: { ...formData, role } });
        } else {
          navigate(pendingRedirect.path);
        }
      } else {
        navigate("/"); // 🏠 Retour page d'accueil
      }
    } catch (err) {
      console.error("❌ Erreur inscription (handleEmailConfirmationOk) :", err);

      if (err.response?.data?.error?.includes("déjà utilisé")) {
        setIsUserExists(true);
        setShowEmailConfirmationModal(false);
      } else {
        setErrors({ form: "Une erreur technique est survenue." });
        showToast("Une erreur technique est survenue.", "error");
      }
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      const userExists = await checkUserExists(formData.email);
      if (userExists) {
        setIsUserExists(true);
        setIsLoading(false);
        return;
      }

      // ❗ ICI : on NE crée plus l'utilisateur
      // On passe juste à l'étape suivante : choix d'adhésion
      setShowAdhesionModal(true);
    } catch (err) {
      console.error("❌ Erreur lors de la vérification utilisateur :", err);
      setErrors({ form: "Une erreur technique est survenue." });
      showToast("Une erreur technique est survenue.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-page">
      {/* 🔔 TOAST */}
      {toast && (
        <div className={`toast-banner toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* HEADER */}
      <div className="register-header">
        <h1>
          Création de compte <span className="highlight">NMS 06</span>
        </h1>
        <p className="subtitle">
          Rejoignez la première plateforme de médiation logement augmentée par
          IA.
        </p>
      </div>

      <div className="register-container">
        {/* COLONNE GAUCHE : INFOS DYNAMIQUES */}
        <div className="register-info-panel">
          {role === "tenant" ? (
            <>
              <h3>Espace Locataire</h3>
              <div className="info-card highlight-card">
                <div className="info-icon">
                  <Home size={24} />
                </div>
                <div>
                  <h4>Défendez vos droits</h4>
                  <p>
                    Signalez des problèmes d'insalubrité, générez des mises en
                    demeure et communiquez avec votre bailleur.
                  </p>
                </div>
              </div>
            </>
          ) : role === "landlord" ? (
            <>
              <h3>Espace Propriétaire</h3>
              <div className="info-card highlight-card">
                <div className="info-icon">
                  <Key size={24} />
                </div>
                <div>
                  <h4>Sécurisez vos biens</h4>
                  <p>
                    Accédez à la médiation pour résoudre les impayés et gérez
                    vos relations locataires sereinement.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <h3>Espace Utilisateur</h3>
              <div className="info-card highlight-card">
                <div className="info-icon">
                  <User size={24} />
                </div>
                <div>
                  <h4>Accès plateforme</h4>
                  <p>
                    Accédez aux outils et aux ressources en tant qu'utilisateur
                    standard (ni locataire, ni propriétaire).
                  </p>
                </div>
              </div>
            </>
          )}

          <ul className="benefits-list">
            <li>
              <CheckCircle size={16} /> Assistance Juridique IA
            </li>
            <li>
              <CheckCircle size={16} /> Modèles de courriers certifiés
            </li>
            <li>
              <CheckCircle size={16} /> Protection des données
            </li>
          </ul>
        </div>

        {/* COLONNE DROITE : FORMULAIRE */}
        <div className="register-form-panel">
          {/* TABS RÔLE */}
          <div className="role-tabs">
            <button
              type="button"
              className={`role-tab ${role === "tenant" ? "active" : ""}`}
              onClick={() => setRole("tenant")}
            >
              <Home size={18} /> Locataire
            </button>
            <button
              type="button"
              className={`role-tab ${role === "landlord" ? "active" : ""}`}
              onClick={() => setRole("landlord")}
            >
              <Key size={18} /> Propriétaire
            </button>
            <button
              type="button"
              className={`role-tab ${role === "user" ? "active" : ""}`}
              onClick={() => setRole("user")}
            >
              <User size={18} /> Standard
            </button>
          </div>

          <form className="register-form" onSubmit={handleSubmit}>
            {/* IDENTITÉ */}
            <div className="form-section-title">Identité</div>
            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Prénom *"
                  className={errors.first_name ? "input-error" : ""}
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Nom *"
                  className={errors.last_name ? "input-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email *"
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && (
                  <span className="error-text">{errors.email}</span>
                )}
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Téléphone *"
                  className={errors.phone ? "input-error" : ""}
                />
              </div>
            </div>

            {/* LOGEMENT */}
            <div className="form-section-title">
              {role === "tenant" ? "Votre Logement" : "Votre Adresse Principale"}
            </div>
            <div className="form-group">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Adresse complète *"
                className={errors.address ? "input-error" : ""}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="Code Postal *"
                  className={errors.postal_code ? "input-error" : ""}
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Ville *"
                  className={errors.city ? "input-error" : ""}
                />
              </div>
            </div>

            {/* CHAMPS SPÉCIFIQUES LOCATAIRE */}
            {role === "tenant" && (
              <div className="tenant-fields fade-in">
                <div className="form-row">
                  <div className="form-group">
                    <select
                      name="landlord_id"
                      value={formData.landlord_id}
                      onChange={handleChange}
                      className={errors.landlord_id ? "input-error" : ""}
                    >
                      <option value="">
                        -- Votre Bailleur (Optionnel) --
                      </option>
                      {Array.isArray(lords) &&
                        lords.map((lord) => (
                          <option key={lord.id} value={lord.id}>
                            {lord.name}
                          </option>
                        ))}
                    </select>
                    {errors.landlord_id && (
                      <span className="error-text">
                        {errors.landlord_id}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      name="tenant_number"
                      value={formData.tenant_number}
                      onChange={handleChange}
                      placeholder="N° Locataire (Optionnel)"
                      className={errors.tenant_number ? "input-error" : ""}
                    />
                    {errors.tenant_number && (
                      <span className="error-text">
                        {errors.tenant_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* SÉCURITÉ */}
            <div className="form-section-title">Sécurité</div>
            <div className="form-group password-group">
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mot de passe *"
                  className={errors.password ? "input-error" : ""}
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              </div>
            </div>

            <div className="form-group password-group">
              <div className="password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirmer mot de passe *"
                  className={errors.confirmPassword ? "input-error" : ""}
                />
                <span
                  className="eye-icon"
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </span>
              </div>
              {errors.confirmPassword && (
                <span className="error-text">{errors.confirmPassword}</span>
              )}
            </div>

            <button
              type="button"
              onClick={handleGeneratePassword}
              className="btn-text-action"
            >
              <RefreshCw size={14} /> Générer un mot de passe sécurisé
            </button>

            {/* ACTIONS */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary full-width"
                disabled={isLoading}
              >
                {isLoading
                  ? "Vérification en cours..."
                  : `Créer mon compte ${
                      role === "tenant"
                        ? "Locataire"
                        : role === "landlord"
                        ? "Propriétaire"
                        : "Utilisateur Standard"
                    }`}
              </button>
            </div>

            <div className="form-footer-link">
              Déjà un compte ? <Link to="/login">Se connecter</Link>
            </div>

            {errors.form && (
              <div className="error-box">
                <AlertCircle size={16} /> {errors.form}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* MODAL : Compte existant */}
      {isUserExists && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon warning">
              <AlertCircle size={32} />
            </div>
            <h3>Compte existant</h3>
            <p>Cet email est déjà enregistré.</p>
            <button
              onClick={() => navigate("/login")}
              className="btn-primary"
            >
              Se connecter
            </button>
            <button
              onClick={() => setIsUserExists(false)}
              className="btn-ghost"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* MODAL : Adhésion */}
      {showAdhesionModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon success">
              <CheckCircle size={32} />
            </div>
            <h3>Compte prêt à être créé ✔</h3>
            <p>
              Voulez-vous devenir <strong>Adhérent</strong> maintenant pour
              débloquer tous les outils IA ?
            </p>
            <div className="modal-actions">
              <button
                onClick={() => handleAdhesionChoice("yes")}
                className="btn-primary"
              >
                Oui, Adhérer (20€)
              </button>
              <button
                onClick={() => handleAdhesionChoice("no")}
                className="btn-secondary"
              >
                Non, plus tard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL : Confirmation d'email */}
      {showEmailConfirmationModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon success">
              <CheckCircle size={32} />
            </div>
            <h3>Confirmez votre inscription NMS06</h3>
            <p>
              En cliquant sur <strong>“J’ai compris”</strong>, votre compte sera
              créé et un email de confirmation sera envoyé à{" "}
              <strong>{formData.email}</strong>.
            </p>
            <p style={{ marginTop: "8px", fontSize: "0.9rem" }}>
              Vous devrez ensuite cliquer sur le lien dans cet email pour
              activer votre compte. Pensez à vérifier vos spams / courriers
              indésirables.
            </p>
            <div className="modal-actions">
              <button
                onClick={handleEmailConfirmationOk}
                className="btn-primary"
                disabled={isSavingUser}
              >
                {isSavingUser ? "Création du compte..." : "J’ai compris"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
